import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useRoleMessages } from '../../contexts/RoleMessagesContext';
import { useToast, ToastContainer } from '../common/Toast';
import { useSocket } from '../../contexts/SocketContext';
import { boothQueueAPI } from '../../services/boothQueue';
import videoCallService from '../../services/videoCall';
import VideoCall from '../VideoCall/VideoCall';
import { FaRedoAlt, FaPlug, FaVideo, FaLinkedin, FaBullhorn } from 'react-icons/fa';
import { getUser } from '../../services/users';
import CallInviteModal from '../VideoCall/CallInviteModal';
import DeviceTestModal from './DeviceTestModal';
import RatingModal from '../MeetingRecords/RatingModal';
import { meetingRecordsAPI } from '../../services/meetingRecords';
import AdminHeader from '../Layout/AdminHeader';
import AdminSidebar from '../Layout/AdminSidebar';
import { useRecruiterBooth } from '../../hooks/useRecruiterBooth';
import { isBoothClosed, formatAvailabilityDate } from '../../utils/availability';
import TestQueueTag from './TestQueueTag';
import {
  MILITARY_EXPERIENCE_LIST,
  JOB_CATEGORY_LIST,
  LANGUAGE_LIST,
  JOB_TYPE_LIST,
  EXPERIENCE_LEVEL_LIST,
  EDUCATION_LEVEL_LIST
} from '../../constants/options';
import './BoothQueueManagement.css';
import '../Dashboard/Dashboard.css';

// How often the queue re-fetches in the background as a fallback for a missed socket update.
const QUEUE_BACKGROUND_REFRESH_MS = 15000;

// Helper function to get label from value using options list
const getLabel = (list, value) => {
  if (!value) return 'Not specified';
  const item = list.find(opt => opt.value === value);
  return item ? item.name : value;
};

// Helper function to get labels for array values
const getLabels = (list, values) => {
  if (!values || !Array.isArray(values) || values.length === 0) return [];
  return values.map(val => {
    const item = list.find(opt => opt.value === val);
    return item ? item.name : val;
  });
};

export default function BoothQueueManagement() {
  const { boothId } = useParams();
  const { user } = useAuth();
  const { getMessage } = useRoleMessages();
  const { socket } = useSocket();
  const { booth: recruiterBooth } = useRecruiterBooth();
  
  // Get role message from context
  const infoBannerMessage = getMessage('meeting-queue', 'info-banner') || '';

  const [booth, setBooth] = useState(null);
  const [event, setEvent] = useState(null);
  // Visible (published/active, not ended) events the user can switch between
  const [availableEvents, setAvailableEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  // Ref mirror so socket handlers (registered once) always see the current selection
  const selectedEventIdRef = useRef(null);
  const [queue, setQueue] = useState([]);
  const [positionFilter, setPositionFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedJobSeeker, setSelectedJobSeeker] = useState(null);
  const [profileModalJobSeeker, setProfileModalJobSeeker] = useState(null); // Full fetched profile (includes linkedInUrl)
  const [showMessages, setShowMessages] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [messages, setMessages] = useState([]);
  const [nowTs, setNowTs] = useState(Date.now());
  const [socketConnected, setSocketConnected] = useState(false);
  const [toast, setToast] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [showMessagesModal, setShowMessagesModal] = useState(false);
  const [currentChatEntry, setCurrentChatEntry] = useState(null);
  const [messageToSend, setMessageToSend] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const { toasts, removeToast, showSuccess, showError, showInfo } = useToast();

  // Video call state
  const [activeCall, setActiveCall] = useState(null);
  const [isInCall, setIsInCall] = useState(false);
  // Recruiter pre-join device selection
  const [showRecruiterInviteModal, setShowRecruiterInviteModal] = useState(false);
  const [pendingQueueEntry, setPendingQueueEntry] = useState(null);
  const [audioInputs, setAudioInputs] = useState([]);
  const [videoInputs, setVideoInputs] = useState([]);
  const [selectedAudioId, setSelectedAudioId] = useState('');
  const [selectedVideoId, setSelectedVideoId] = useState('');
  const [showDeviceModal, setShowDeviceModal] = useState(false);

  // Rating modal state
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingModalData, setRatingModalData] = useState(null);
  const [submittingRating, setSubmittingRating] = useState(false);
  const messagesEndRef = useRef(null);

  // Theme colors from event (fallbacks provided)
  const theme = useMemo(() => ({
    primary: event?.theme?.primaryColor || '#3b82f6',
    success: event?.theme?.successColor || '#10b981',
    danger: event?.theme?.dangerColor || '#ef4444',
    warningBg: '#fef3c7',
    warningText: '#92400e',
  }), [event]);

  // Filter options come from the booth's configured open positions, plus whatever
  // queue entries were recorded with in case the booth edited its positions later.
  const boothOpenPositions = useMemo(() => {
    const configured = (booth || recruiterBooth)?.openPositions;
    return Array.isArray(configured) ? configured : [];
  }, [booth, recruiterBooth]);

  const positionFilterOptions = useMemo(() => {
    const titles = [
      ...boothOpenPositions.map(position => position?.title),
      ...queue.map(entry => entry?.appliedPosition)
    ];
    return [...new Set(titles.filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }, [boothOpenPositions, queue]);

  const locationFilterOptions = useMemo(() => {
    const configured = boothOpenPositions;
    const scoped = positionFilter === 'all'
      ? configured
      : configured.filter(position => position?.title === positionFilter);
    const labels = [
      ...scoped.flatMap(position => (Array.isArray(position?.locations) ? position.locations : [])),
      ...queue
        .filter(entry => positionFilter === 'all' || entry?.appliedPosition === positionFilter)
        .map(entry => entry?.appliedLocation)
    ];
    return [...new Set(labels.filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }, [boothOpenPositions, queue, positionFilter]);

  // Drop a location filter that no longer applies to the selected position
  useEffect(() => {
    if (locationFilter !== 'all' && !locationFilterOptions.includes(locationFilter)) {
      setLocationFilter('all');
    }
  }, [locationFilterOptions, locationFilter]);

  const filteredQueue = useMemo(() => queue.filter((entry) => {
    if (positionFilter !== 'all' && (entry?.appliedPosition || '') !== positionFilter) return false;
    if (locationFilter !== 'all' && (entry?.appliedLocation || '') !== locationFilter) return false;
    return true;
  }), [queue, positionFilter, locationFilter]);

  const hasQueueFilters = positionFilterOptions.length > 0 || locationFilterOptions.length > 0;
  const isQueueFiltered = positionFilter !== 'all' || locationFilter !== 'all';

  useEffect(() => {
    // Wait for recruiter booth to load if boothId is not in URL
    if (!boothId && !recruiterBooth) {
      return;
    }
    loadData();
    joinSocketRoom();

    return () => {
      if (socket) {
        const targetBoothId = boothId || recruiterBooth?._id;
        if (targetBoothId) {
          socket.emit('leave-booth-management', { boothId: targetBoothId, userId: user?._id });
        }
      }
    };
  }, [boothId, recruiterBooth?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ensure we (re)join socket rooms after the socket connects/reconnects
  useEffect(() => {
    if (!socket) {
      setSocketConnected(false);
      return;
    }

    const handleConnect = () => {
      setSocketConnected(true);
      // Re-join rooms for this booth so we receive live updates
      joinSocketRoom();
    };

    const handleDisconnect = () => {
      setSocketConnected(false);
    };

    // Reflect current state immediately
    setSocketConnected(!!socket.connected);
    if (socket.connected) {
      // If already connected (hot reload/navigation), ensure we are in rooms
      joinSocketRoom();
    }

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
    };
  }, [socket, boothId, recruiterBooth?._id, user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!socket || !socket.connected) {
      setSocketConnected(false);
      return;
    }

    setSocketConnected(true);
    socket.on('queue-updated', handleQueueUpdate);
    socket.on('new-queue-message', handleNewMessage);
    socket.on('jobseeker-left-with-message', handleJobSeekerLeftWithMessage);
    socket.on('test-connection-response', () => {});

    return () => {
      socket.off('queue-updated', handleQueueUpdate);
      socket.off('new-queue-message', handleNewMessage);
      socket.off('jobseeker-left-with-message', handleJobSeekerLeftWithMessage);
      socket.off('test-connection-response');
    };
  }, [socket?.connected]); // eslint-disable-line react-hooks/exhaustive-deps

  // Safety net for the queue list.
  //
  // 'queue-updated' only reaches recruiters connected to the same server process that handled
  // the job seeker's join: there is no Socket.IO Redis adapter, so the event does not cross
  // instances. If that event is missed the queue sits silently stale and the recruiter sees
  // "No job seekers in queue" while somebody is actually waiting. Re-fetch periodically, and
  // whenever the tab regains focus, so the list cannot stay wrong indefinitely.
  //
  // Deliberately quiet: no toast, no spinner, no focus change — it only replaces the same
  // list data the socket handler already replaces, so it adds no new screen-reader noise.
  useEffect(() => {
    const targetBoothId = boothId || recruiterBooth?._id;
    if (!targetBoothId) return undefined;

    let cancelled = false;

    // Computed here rather than read from canViewUnscopedQueue: that const is declared further
    // down the component body, so naming it in this effect's dependency array would evaluate it
    // during render while it is still in the temporal dead zone.
    const canViewUnscoped = ['Admin', 'GlobalSupport'].includes(user?.role);

    const silentRefreshQueue = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      const eventIdForQueue = selectedEventIdRef.current;
      if (!eventIdForQueue && !canViewUnscoped) return;
      try {
        const res = await boothQueueAPI.getBoothQueue(targetBoothId, eventIdForQueue || undefined);
        if (!cancelled && res?.success) setQueue(res.queue);
      } catch (error) {
        // Background refresh: stay quiet and try again on the next tick.
      }
    };

    const intervalId = setInterval(silentRefreshQueue, QUEUE_BACKGROUND_REFRESH_MS);
    window.addEventListener('focus', silentRefreshQueue);
    document.addEventListener('visibilitychange', silentRefreshQueue);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      window.removeEventListener('focus', silentRefreshQueue);
      document.removeEventListener('visibilitychange', silentRefreshQueue);
    };
  }, [boothId, recruiterBooth?._id, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tick every second for live waiting timers
  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const formatDuration = (ms) => {
    const total = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
  };

  const formatEventDateTime = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const eventStatusLabel = (status) => {
    if (!status) return null;
    const labels = {
      draft: 'Draft',
      published: 'Published',
      active: 'Active',
      completed: 'Completed',
      cancelled: 'Cancelled'
    };
    return labels[status] || status;
  };


  // Admin/GlobalSupport may still view the full booth queue when no event
  // passes the visibility filter; recruiters may not.
  const canViewUnscopedQueue = ['Admin', 'GlobalSupport'].includes(user?.role);

  const loadData = async () => {
    try {
      setLoading(true);

      // Use boothId from URL if available, otherwise use recruiter's booth
      const targetBoothId = boothId || recruiterBooth?._id;
      
      if (!targetBoothId) {
        console.error('No booth ID available');
        setLoading(false);
        return;
      }

      const boothRes = await axios.get(`/api/booths/${targetBoothId}`);
      const boothData = boothRes.data;

      let eventIdForQueue = null;

      if (boothData && boothData.booth) {
        setBooth(boothData.booth);

        const visibleEvents = Array.isArray(boothData.events) ? boothData.events : [];
        setAvailableEvents(visibleEvents);

        // Keep the current selection if it is still visible, otherwise default to the first visible event
        const currentId = selectedEventIdRef.current;
        const stillVisible = currentId && visibleEvents.some(e => String(e._id) === String(currentId));
        const nextId = stillVisible
          ? String(currentId)
          : (visibleEvents[0] ? String(visibleEvents[0]._id) : null);
        selectedEventIdRef.current = nextId;
        setSelectedEventId(nextId);
        eventIdForQueue = nextId;

        const selected = visibleEvents.find(e => String(e._id) === String(nextId));
        if (selected) {
          setEvent(selected);
        } else if (canViewUnscopedQueue && boothData.event) {
          setEvent(boothData.event);
        } else {
          setEvent(null);
        }
      } else {
        console.error('Failed to load booth:', boothData.message || boothData.error);
        // Fallback to recruiter booth if API call fails (event intentionally not
        // taken from the hook: it could be expired/unpublished)
        if (recruiterBooth) {
          setBooth(recruiterBooth);
        }
      }

      if (eventIdForQueue || canViewUnscopedQueue) {
        const queueRes = await boothQueueAPI.getBoothQueue(targetBoothId, eventIdForQueue || undefined);
        if (queueRes.success) {
          setQueue(queueRes.queue);
        } else {
          console.error('Failed to load queue:', queueRes.message);
        }
      } else {
        // No visible event for this user: don't show queue entries from hidden events
        setQueue([]);
      }

    } catch (error) {
      console.error('Error loading data:', error);
      // Fallback to recruiter booth on error
      if (recruiterBooth) {
        setBooth(recruiterBooth);
      }
    } finally {
      setLoading(false);
    }
  };

  const joinSocketRoom = async () => {
    const targetBoothId = boothId || recruiterBooth?._id;
    if (!socket || !targetBoothId || !user) {
      setToast('Cannot reconnect: Missing connection details');
      setTimeout(() => setToast(''), 3000);
      return;
    }

    try {
      setIsReconnecting(true);
      socket.emit('join-booth-management', { boothId: targetBoothId, userId: user._id });
      socket.emit('join-booth-queue', { boothId: targetBoothId, userId: user._id });

      setTimeout(() => {
        socket.emit('test-connection', { message: 'Testing after room join' });
        setToast('Reconnected to real-time updates');
        setTimeout(() => setToast(''), 2000);
      }, 1000);
    } catch (error) {
      console.error('Error joining socket rooms:', error);
      setToast('Failed to reconnect');
      setTimeout(() => setToast(''), 3000);
    } finally {
      setIsReconnecting(false);
    }
  };

  // Helper to check if a queue entry's event is in user's assigned events
  // Returns true if user should see this queue entry (Admin/GlobalSupport see all, Recruiters see only their assigned events)
  const shouldProcessQueueEntry = (queueEntry) => {
    // Ignore updates for events other than the one currently being viewed
    const selectedId = selectedEventIdRef.current;
    const entryEventId = queueEntry?.event?._id || queueEntry?.event;
    if (selectedId && entryEventId && String(entryEventId) !== String(selectedId)) {
      return false;
    }

    // Admin and GlobalSupport can see all queue entries
    if (['Admin', 'GlobalSupport'].includes(user?.role)) {
      return true;
    }
    
    // For Recruiters with assigned events, check if queue entry's event is in their assigned events
    if (user?.role === 'Recruiter' && user?.assignedEvents && user.assignedEvents.length > 0) {
      const queueEventId = queueEntry?.event?._id || queueEntry?.event;
      if (!queueEventId) return true; // If no event info, allow it (will be filtered by API anyway)
      
      const eventIdStr = queueEventId.toString();
      const assignedEventIds = user.assignedEvents.map(e => (e?._id || e).toString());
      return assignedEventIds.includes(eventIdStr);
    }
    
    // Default: allow (Recruiters without assigned events see all)
    return true;
  };

  const normalizeId = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      return value._id?.toString?.() || value.id?.toString?.() || value.toString?.() || '';
    }
    return String(value);
  };

  const handleQueueUpdate = (data) => {
    const targetBoothId = normalizeId(boothId || recruiterBooth?._id);
    const eventBoothId = normalizeId(data?.boothId || data?.booth || data?.queueEntry?.booth);
    if (!targetBoothId || !eventBoothId || targetBoothId !== eventBoothId) return;

    // Filter socket events by assigned events for recruiters
    if (data.queueEntry && !shouldProcessQueueEntry(data.queueEntry)) {
      console.log('Ignoring queue update for event not in assigned events');
      return;
    }
    loadQueueData();
  };

  const loadQueueData = async () => {
    try {
      setIsRefreshing(true);
      // Use boothId from URL if available, otherwise use recruiter's booth
      const targetBoothId = boothId || recruiterBooth?._id;
      if (!targetBoothId) {
        console.error('No booth ID available for loading queue data');
        return;
      }
      const eventIdForQueue = selectedEventIdRef.current;
      if (!eventIdForQueue && !canViewUnscopedQueue) {
        setQueue([]);
        return;
      }
      const queueRes = await boothQueueAPI.getBoothQueue(targetBoothId, eventIdForQueue || undefined);
      if (queueRes.success) {
        setQueue(queueRes.queue);
        setToast('Queue data refreshed successfully');
        setTimeout(() => setToast(''), 2000);
      } else {
        setToast('Failed to refresh queue data');
        setTimeout(() => setToast(''), 3000);
      }
    } catch (error) {
      console.error('Error reloading queue data:', error);
      setToast('Error refreshing queue data');
      setTimeout(() => setToast(''), 3000);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Switch the event whose queue is being viewed
  const handleEventChange = (nextEventId) => {
    const normalized = nextEventId ? String(nextEventId) : null;
    selectedEventIdRef.current = normalized;
    setSelectedEventId(normalized);
    const selected = availableEvents.find(e => String(e._id) === String(normalized));
    setEvent(selected || null);
    loadQueueData();
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Auto-scroll to bottom when messages change and modal is open
  useEffect(() => {
    if (showMessagesModal && messages.length > 0) {
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [messages, showMessagesModal]);

  const handleNewMessage = (data) => {
    const targetBoothId = boothId || recruiterBooth?._id;
    if (data.boothId === targetBoothId || data.boothId?.toString() === targetBoothId?.toString()) {
      // Filter socket events by assigned events for recruiters
      if (data.queueEntry && !shouldProcessQueueEntry(data.queueEntry)) {
        console.log('Ignoring new message for event not in assigned events');
        return;
      }
      
      playNotificationSound();
      const jobSeekerName = data.queueEntry?.jobSeeker?.name || 'job seeker';
      showInfo(`New message from ${jobSeekerName}`, 200);
      
      const queueEntryId = (data.queueEntry?._id || data.queueEntry?.id)?.toString()?.trim();
      const currentChatId = (currentChatEntry?._id || currentChatEntry?.id)?.toString()?.trim();
      
      const shouldAddMessage = queueEntryId && (
        (showMessagesModal && queueEntryId === currentChatId) ||
        !showMessagesModal
      );
      
      if (shouldAddMessage) {
        setMessages(prev => {
          const incomingCreatedAt = data.message.createdAt instanceof Date 
            ? data.message.createdAt.getTime() 
            : new Date(data.message.createdAt).getTime();
          
          const isDuplicate = prev.some(msg => {
            if (msg._id && data.message._id) {
              return msg._id.toString() === data.message._id.toString();
            }
            
            const msgCreatedAt = msg.createdAt instanceof Date 
              ? msg.createdAt.getTime() 
              : new Date(msg.createdAt).getTime();
            
            const timeDiff = Math.abs(msgCreatedAt - incomingCreatedAt);
            return msg.content === data.message.content && 
                   msg.sender === data.message.sender &&
                   timeDiff < 5000;
          });
          
          if (isDuplicate) {
            return prev;
          }
          
          return [...prev, data.message];
        });
        
        if (showMessagesModal) {
          setTimeout(() => {
            scrollToBottom();
          }, 100);
        }
      }
      
      loadQueueData();
    }
  };

  const handleJobSeekerLeftWithMessage = (data) => {
    // Only show notification if the queue entry was in our filtered queue
    // (this ensures recruiters only see notifications for their assigned events)
    const queueEntryInCurrentList = queue.some(entry => 
      (entry._id || entry.id)?.toString() === data.queueEntryId?.toString()
    );
    
    if (queueEntryInCurrentList) {
      showInfo(`${data.jobSeekerName} left a ${data.messageType} message and exited the queue`);
    }
    
    loadQueueData(); // Refresh queue to show left_with_message status
  };

  const handleSendMessage = async () => {
    if (!messageToSend.trim() || !currentChatEntry) return;

    try {
      setIsSendingMessage(true);
      await boothQueueAPI.sendMessageToJobSeeker(currentChatEntry._id, messageToSend);
      
      // Add message to local state immediately for instant feedback
      const newMessage = {
        type: 'text',
        content: messageToSend,
        sender: 'recruiter',
        createdAt: new Date(),
        isRead: true
      };
      setMessages(prev => [...prev, newMessage]);
      setMessageToSend('');
      // Removed toast notification for message sent
      
      // Scroll to bottom
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error('Error sending message to job seeker:', error);
      showError('Failed to send message');
    } finally {
      setIsSendingMessage(false);
    }
  };

  const closeBroadcastModal = () => {
    setShowBroadcastModal(false);
    setBroadcastMessage('');
  };

  const handleBroadcastMessage = async () => {
    const targetBoothId = boothId || recruiterBooth?._id;
    if (!broadcastMessage.trim() || !targetBoothId || queue.length === 0) return;

    try {
      setIsBroadcasting(true);
      const response = await boothQueueAPI.broadcastMessage(
        targetBoothId,
        broadcastMessage.trim(),
        selectedEventId || undefined
      );
      const count = response.recipientCount ?? 0;
      if (count === 0) {
        showInfo(response.message || 'No job seekers currently in the queue');
      } else {
        showSuccess(response.message || `Message sent to ${count} job seeker${count === 1 ? '' : 's'}`);
      }
      closeBroadcastModal();
    } catch (error) {
      console.error('Error broadcasting message:', error);
      showError(error.response?.data?.message || 'Failed to broadcast message');
    } finally {
      setIsBroadcasting(false);
    }
  };

  const playNotificationSound = () => {
    try {
      // Create a simple notification beep using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (err) {
      console.log('Could not play notification sound:', err);
    }
  };

  const enumerateDevicesForRecruiter = async () => {
    try {
      try { await navigator.mediaDevices.getUserMedia({ audio: true, video: true }); } catch (e) { }
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audios = devices.filter(d => d.kind === 'audioinput');
      const videos = devices.filter(d => d.kind === 'videoinput');
      setAudioInputs(audios);
      setVideoInputs(videos);
      const savedAudio = localStorage.getItem('preferredAudioDeviceId');
      const savedVideo = localStorage.getItem('preferredVideoDeviceId');
      setSelectedAudioId(savedAudio || audios[0]?.deviceId || '');
      setSelectedVideoId(savedVideo || videos[0]?.deviceId || '');
    } catch (err) {
      console.error('Failed to enumerate devices (recruiter):', err);
    }
  };

  const handleDeviceSelection = async () => {
    let stream = null;
    try {
      // Request permissions and enumerate devices
      stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audios = devices.filter(d => d.kind === 'audioinput');
      const videos = devices.filter(d => d.kind === 'videoinput');

      setAudioInputs(audios);
      setVideoInputs(videos);

      // Load saved preferences
      const savedAudio = localStorage.getItem('preferredAudioDeviceId');
      const savedVideo = localStorage.getItem('preferredVideoDeviceId');
      setSelectedAudioId(savedAudio || audios[0]?.deviceId || '');
      setSelectedVideoId(savedVideo || videos[0]?.deviceId || '');

      setShowDeviceModal(true);
    } catch (error) {
      console.error('Error accessing devices:', error);
      showError('Unable to access camera and microphone. Please check your permissions.');
    } finally {
      // Always stop the stream tracks to release camera and microphone
      if (stream) {
        stream.getTracks().forEach(track => {
          track.stop();
        });
      }
    }
  };

  const handleDeviceSave = () => {
    if (selectedAudioId) {
      localStorage.setItem('preferredAudioDeviceId', selectedAudioId);
    }
    if (selectedVideoId) {
      localStorage.setItem('preferredVideoDeviceId', selectedVideoId);
    }

    setShowDeviceModal(false);
    showSuccess('Camera and microphone preferences saved successfully!');
  };

  const handleDeviceCancel = () => {
    setShowDeviceModal(false);
  };

  const handleInviteToMeeting = async (queueEntry) => {
    // Test entries (booth test link) can be invited whatever the booth's close time
    if (!queueEntry?.isTest && isBoothClosed(booth || recruiterBooth)) {
      showError('This booth is closed. You can no longer start meetings from this queue.');
      return;
    }
    setPendingQueueEntry(queueEntry);
    await enumerateDevicesForRecruiter();
    setShowRecruiterInviteModal(true);
  };

  const handleRecruiterAcceptInvite = async () => {
    try {
      if (selectedAudioId) localStorage.setItem('preferredAudioDeviceId', selectedAudioId);
      if (selectedVideoId) localStorage.setItem('preferredVideoDeviceId', selectedVideoId);
      setShowRecruiterInviteModal(false);
      if (!pendingQueueEntry) return;

      const queueEntry = pendingQueueEntry;
      setPendingQueueEntry(null);

      // Create video call after device selection
      const callResponse = await videoCallService.createCall(queueEntry._id);
      if (callResponse.success) {
        setActiveCall({
          id: callResponse.callId,
          roomName: callResponse.roomName,
          accessToken: callResponse.accessToken,
          userRole: 'recruiter',
          booth: callResponse.booth,
          event: callResponse.event,
          participants: {
            recruiter: user,
            jobSeeker: callResponse.jobSeeker
          },
          metadata: {
            interpreterRequested: callResponse.interpreterRequested,
            interpreterCategory: callResponse.interpreterCategory,
            isTestCall: !!callResponse.isTestCall
          }
        });
        setIsInCall(true);
      }
    } catch (error) {
      console.error('Error creating video call:', error);
      const data = error.response?.data;
      if (data?.error === 'BOOTH_CLOSED') {
        showError(data.message);
        loadData(); // Pull the new close time so the queue locks itself
        return;
      }
      alert('Failed to start video call with job seeker');
    }
  };

  const handleRecruiterDeclineInvite = () => {
    setShowRecruiterInviteModal(false);
    setPendingQueueEntry(null);
  };

  const handleCallEnd = async (endData = {}) => {
    const declinedByJobSeeker = endData?.reason === 'declined_invitation';
    const recruiterDisconnected = endData?.reason === 'recruiter_disconnected';

    if (declinedByJobSeeker) {
      showInfo('Job seeker declined the call invitation and remains in queue.');
      setActiveCall(null);
      setIsInCall(false);
      loadData();
      return;
    }

    if (recruiterDisconnected) {
      showInfo('Call ended after recruiter disconnected. Job seeker returned to queue.');
      setActiveCall(null);
      setIsInCall(false);
      loadData();
      return;
    }

    try {
      // Create meeting record from the video call
      if (activeCall && (activeCall.id || activeCall.callId)) {
        const videoCallId = activeCall.id || activeCall.callId;
        await meetingRecordsAPI.createFromVideoCall(videoCallId);
      }
    } catch (error) {
      console.error('Error creating meeting record:', error);
      // Don't show error toast - we'll still show rating modal
    }

    // Always show rating modal for recruiters, regardless of meeting record creation success
    if (user.role === 'Recruiter' && activeCall) {
      const jobSeekerName = activeCall.jobSeekerName || 
                           activeCall.jobSeeker?.name || 
                           'Job Seeker';
      
      setRatingModalData({
        meetingId: null, // Will be set after meeting record is created
        videoCallId: activeCall.id || activeCall.callId,
        jobSeekerName: jobSeekerName
      });
      setShowRatingModal(true);
    }

    setActiveCall(null);
    setIsInCall(false);
    // Refresh queue data to see updated statuses
    loadData();
  };

  const showToast = (message, type = 'info') => {
    setToast(message);
    setTimeout(() => setToast(''), 3000);
  };

  const handleRatingSubmit = async ({ rating, feedback }) => {
    try {
      setSubmittingRating(true);
      
      // First, get the meeting record ID by creating it from the video call
      if (ratingModalData?.videoCallId) {
        const meetingRecord = await meetingRecordsAPI.createFromVideoCall(ratingModalData.videoCallId);
        
        // Submit the rating
        await meetingRecordsAPI.submitRating(meetingRecord._id, rating, feedback);
        
        showToast('Rating submitted successfully!', 'success');
        setShowRatingModal(false);
        setRatingModalData(null);
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      showToast('Failed to submit rating. Please try again.', 'error');
    } finally {
      setSubmittingRating(false);
    }
  };

  const handleRatingClose = () => {
    if (!submittingRating) {
      setShowRatingModal(false);
      setRatingModalData(null);
    }
  };

  const handleRemoveFromQueue = async (queueEntry) => {
    const jobSeekerName = queueEntry.jobSeeker?.name || 'this job seeker';
    const inCall = queueEntry.status === 'in_meeting' || queueEntry.status === 'invited' || queueEntry.isInCall;
    const confirmMsg = inCall
      ? `Remove ${jobSeekerName} from the queue? This will end the active video call if one is in progress.`
      : `Remove ${jobSeekerName} from the queue?`;
    if (window.confirm(confirmMsg)) {
      try {
        await boothQueueAPI.removeFromQueue(queueEntry._id);
        loadData(); // Refresh queue
      } catch (error) {
        console.error('Error removing from queue:', error);
        alert('Failed to remove job seeker from queue');
      }
    }
  };

  const handleViewMessages = async (queueEntry) => {
    const previousChatEntry = currentChatEntry;
    setCurrentChatEntry(queueEntry);
    setShowMessagesModal(true);
    try {
      const response = await boothQueueAPI.getQueueMessages(queueEntry._id);
      if (response.success) {
        const apiMessages = response.messages || [];
        
        setMessages(prev => {
          if (previousChatEntry?._id !== queueEntry._id) {
            return apiMessages;
          }
          
          const mergedMessages = [...apiMessages];
          const existingIds = new Set(apiMessages.map(m => m._id || `${m.content}-${m.createdAt}-${m.sender}`));
          
          prev.forEach(msg => {
            const msgId = msg._id || `${msg.content}-${msg.createdAt}-${msg.sender}`;
            if (!existingIds.has(msgId)) {
              mergedMessages.push(msg);
            }
          });
          
          return mergedMessages.sort((a, b) => {
            const timeA = new Date(a.createdAt || a.timestamp || 0).getTime();
            const timeB = new Date(b.createdAt || b.timestamp || 0).getTime();
            return timeA - timeB;
          });
        });
        
        setTimeout(scrollToBottom, 100);
        loadQueueData();
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      showError('Error loading messages');
    }
  };

  if (loading) {
    return (
      <div className="dashboard">
        <a href="#main-content" className="skip-link">Skip to main content</a>
        <AdminHeader
          brandingLogo={event?.logoUrl || event?.logo || ''}
          brandingLogoAlt={event?.logoAltText || ''}
          secondaryLogo={booth?.logoUrl || booth?.companyLogo || ''}
          secondaryLogoAlt={booth?.logoAltText || ''}
        />
        <div className="dashboard-layout">
          <AdminSidebar active="queue" />
          <main id="main-content" className="dashboard-main" tabIndex={-1} aria-label="main content">
            <div className="loading">Loading booth queue...</div>
          </main>
        </div>
      </div>
    );
  }


  // Use booth from state if available, otherwise fallback to hook data.
  // The event always comes from state so hidden (expired/unpublished) events
  // from the hook are never displayed.
  const displayBooth = booth || recruiterBooth;
  const displayEvent = event;
  // nowTs ticks every second, so the queue locks itself the moment close time passes.
  const boothClosed = isBoothClosed(displayBooth, new Date(nowTs));
  const boothClosedNotice = boothClosed
    ? `This booth closed on ${formatAvailabilityDate(displayBooth.closeTime)}. You can no longer start meetings with job seekers in this queue.`
    : '';
  const hasTestEntries = queue.some(entry => entry?.isTest);

  return (
    <div className="dashboard">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <AdminHeader
        brandingLogo={displayEvent?.logoUrl || displayEvent?.logo || ''}
        brandingLogoAlt={displayEvent?.logoAltText || ''}
        secondaryLogo={displayBooth?.logoUrl || displayBooth?.companyLogo || ''}
        secondaryLogoAlt={displayBooth?.logoAltText || ''}
      />
      <div className="dashboard-layout">
        <AdminSidebar active="queue" />
        <main id="main-content" className="dashboard-main" tabIndex={-1} aria-label="main content">
          <div className="booth-queue-management">
            {/* Header */}
            {infoBannerMessage && (
              <div className="info-banner" style={{ marginBottom: '1.5rem' }}>
                <span>{infoBannerMessage}</span>
              </div>
            )}
            {boothClosed && (
              <div className="booth-closed-notice" role="status" aria-live="polite">
                <span>
                  {boothClosedNotice}
                  {hasTestEntries ? ' Job seekers marked Test joined with the booth\'s test link and can still be invited.' : ''}
                </span>
              </div>
            )}
            <div className="management-header">
              <div className="booth-info">
                <div className="booth-logo">
                  {(displayBooth?.logoUrl || displayBooth?.companyLogo) && (
                    <img src={displayBooth?.logoUrl || displayBooth?.companyLogo} alt={`${displayBooth?.name || displayBooth?.company} logo`} />
                  )}
                </div>
                <div>
                  <h1>{displayBooth?.name || displayBooth?.company} - Meeting Queue</h1>
                  <p>{displayEvent?.name}</p>
                  {/* Compact stats in header */}
                  <div className="header-stats">
                    <div className="header-stat">
                      <span className="stat-label">Total</span>
                      <span className="stat-value">{queue.length}</span>
                    </div>
                    <div className="header-stat">
                      <span className="stat-label">In Call</span>
                      <span className="stat-value">{queue.filter(q => q.status === 'in_meeting' || q.isInCall).length}</span>
                    </div>
                    <div className="header-stat">
                      <span className="stat-label">Waiting</span>
                      <span className="stat-value">{queue.filter(q => q.status !== 'in_meeting' && !q.isInCall).length}</span>
                    </div>
                  </div>
                  {(formatEventDateTime(displayBooth?.openTime)
                    || formatEventDateTime(displayBooth?.recruiterEndTime)
                    || formatEventDateTime(displayBooth?.closeTime)) && (
                    <dl className="booth-header-availability" aria-label="Booth availability">
                      {formatEventDateTime(displayBooth?.openTime) && (
                        <div className="booth-header-availability-item">
                          <dt>Opens</dt>
                          <dd>
                            <time dateTime={displayBooth.openTime}>
                              {formatEventDateTime(displayBooth.openTime)}
                            </time>
                          </dd>
                        </div>
                      )}
                      {formatEventDateTime(displayBooth?.recruiterEndTime) && (
                        <div className="booth-header-availability-item">
                          <dt>Recruiters unavailable after</dt>
                          <dd>
                            <time dateTime={displayBooth.recruiterEndTime}>
                              {formatEventDateTime(displayBooth.recruiterEndTime)}
                            </time>
                          </dd>
                        </div>
                      )}
                      {formatEventDateTime(displayBooth?.closeTime) && (
                        <div className="booth-header-availability-item">
                          <dt>Closes</dt>
                          <dd>
                            <time dateTime={displayBooth.closeTime}>
                              {formatEventDateTime(displayBooth.closeTime)}
                            </time>
                          </dd>
                        </div>
                      )}
                    </dl>
                  )}
                </div>
              </div>

              <div className="serving-controls">
                <div className="serving-controls-row">
                <span className={`rt-badge ${socketConnected ? 'online' : 'offline'}`} title={socketConnected ? 'Real-time connected' : 'Real-time offline'}>
                  <span className="rt-dot" /> {socketConnected ? 'Online' : 'Offline'}
                </span>
                <button
                  onClick={loadQueueData}
                  className={`icon-btn refresh-btn ${isRefreshing ? 'loading' : ''}`}
                  title="Refresh Queue Data"
                  aria-label="Refresh Queue Data"
                  disabled={isRefreshing}
                >
                  <FaRedoAlt size={16} />
                </button>
                <button
                  onClick={joinSocketRoom}
                  className={`icon-btn reconnect-btn ${isReconnecting ? 'loading' : ''}`}
                  title="Reconnect to Real-time Updates"
                  aria-label="Reconnect to Real-time Updates"
                  disabled={isReconnecting}
                >
                  <FaPlug size={16} />
                </button>
                <button
                    onClick={handleDeviceSelection}
                    className="test-device-btn"
                    title="Test Camera & Microphone"
                    aria-label="Test camera and microphone devices"
                  >
                    <FaVideo size={16} aria-hidden="true" /> Test Device
                  </button>
                <button
                    onClick={() => setShowBroadcastModal(true)}
                    className="broadcast-message-btn"
                    title="Broadcast message to all job seekers in queue"
                    aria-label="Broadcast message to all job seekers in queue"
                    disabled={queue.length === 0}
                    type="button"
                  >
                    <FaBullhorn size={16} aria-hidden="true" /> Broadcast Message
                  </button>
                </div>
              </div>
            </div>

            {/* Stats moved to header; removed cards */}

            {/* Queue List */}
            <div className="queue-list">
              <section
                className="queue-event-card"
                aria-label="Event summary"
              >
                {displayEvent ? (
                  <div className="queue-event-card-inner">
                    <div className="queue-event-card-brand">
                      {displayEvent.logoUrl ? (
                        <img
                          src={displayEvent.logoUrl}
                          alt={displayEvent.logoAltText || `${displayEvent.name} logo`}
                          className="queue-event-card-logo"
                        />
                      ) : (
                        <div className="queue-event-card-logo-placeholder" aria-hidden="true">
                          Event
                        </div>
                      )}
                    </div>
                    <div className="queue-event-card-body">
                      <div className="queue-event-card-heading">
                        <div>
                          <p className="queue-event-card-kicker">Event</p>
                          <h2 id="queue-event-card-title" className="queue-event-card-title">
                            {displayEvent.name}
                          </h2>
                        </div>
                        {availableEvents.length > 1 && (
                          <div className="queue-event-switcher">
                            <label htmlFor="queue-event-select">Switch event</label>
                            <select
                              id="queue-event-select"
                              value={selectedEventId || ''}
                              onChange={(e) => handleEventChange(e.target.value)}
                            >
                              {availableEvents.map((ev) => (
                                <option key={ev._id} value={ev._id}>
                                  {ev.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                      <dl className="queue-event-card-meta">
                        {formatEventDateTime(displayEvent.start) && (
                          <div className="queue-event-card-meta-item">
                            <dt>Opens</dt>
                            <dd>
                              <time dateTime={displayEvent.start}>
                                {formatEventDateTime(displayEvent.start)}
                              </time>
                            </dd>
                          </div>
                        )}
                        {formatEventDateTime(displayEvent.end) && (
                          <div className="queue-event-card-meta-item">
                            <dt>Closes</dt>
                            <dd>
                              <time dateTime={displayEvent.end}>
                                {formatEventDateTime(displayEvent.end)}
                              </time>
                            </dd>
                          </div>
                        )}
                        {displayEvent.status && (
                          <div className="queue-event-card-meta-item">
                            <dt>Status</dt>
                            <dd>
                              <span className="queue-event-card-status">{eventStatusLabel(displayEvent.status)}</span>
                            </dd>
                          </div>
                        )}
                        {displayEvent.link && (
                          <div className="queue-event-card-meta-item queue-event-card-meta-item--link">
                            <dt>Event link</dt>
                            <dd>
                              <a
                                href={displayEvent.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="queue-event-card-link"
                              >
                                {displayEvent.link}
                              </a>
                            </dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  </div>
                ) : (
                  <div className="queue-event-card-inner queue-event-card-inner--empty">
                    <p className="queue-event-card-missing">
                      {availableEvents.length === 0
                        ? 'No active event is assigned to this booth. Events that have ended or are not published are hidden.'
                        : 'Event information is not available for this booth.'}
                    </p>
                  </div>
                )}
              </section>

              <h2 className="queue-seekers-heading">Job Seekers in Queue</h2>

              {hasQueueFilters && (
                <div className="queue-filters" role="group" aria-label="Filter queue by position and location">
                  <div className="queue-filter">
                    <label htmlFor="queue-position-filter">Position</label>
                    <select
                      id="queue-position-filter"
                      value={positionFilter}
                      onChange={(e) => setPositionFilter(e.target.value)}
                    >
                      <option value="all">All positions</option>
                      {positionFilterOptions.map((title) => (
                        <option key={title} value={title}>{title}</option>
                      ))}
                    </select>
                  </div>
                  <div className="queue-filter">
                    <label htmlFor="queue-location-filter">Location</label>
                    <select
                      id="queue-location-filter"
                      value={locationFilter}
                      onChange={(e) => setLocationFilter(e.target.value)}
                      disabled={locationFilterOptions.length === 0}
                    >
                      <option value="all">All locations</option>
                      {locationFilterOptions.map((location) => (
                        <option key={location} value={location}>{location}</option>
                      ))}
                    </select>
                  </div>
                  {isQueueFiltered && (
                    <button
                      type="button"
                      className="queue-filter-clear"
                      onClick={() => { setPositionFilter('all'); setLocationFilter('all'); }}
                    >
                      Clear filters
                    </button>
                  )}
                  <p className="queue-filter-count" aria-live="polite">
                    Showing {filteredQueue.length} of {queue.length} job seeker{queue.length === 1 ? '' : 's'}
                  </p>
                </div>
              )}

              {filteredQueue.length === 0 ? (
                <div className="empty-queue">
                  <p>{queue.length > 0 && isQueueFiltered ? 'No job seekers match the selected filters' : 'No job seekers in queue'}</p>
                </div>
              ) : (
                <div className="queue-grid">
                  {filteredQueue.map((queueEntry) => {
                    // Skip entries with null jobSeeker
                    if (!queueEntry.jobSeeker) {
                      return null;
                    }
                    
                    // A test entry is never locked by the booth's close time
                    const inviteLocked = boothClosed && !queueEntry.isTest;

                    return (
                    <div
                      key={queueEntry._id}
                      className={`queue-card ${queueEntry.status === 'left_with_message' ? 'left-message' : queueEntry.status === 'in_meeting' || queueEntry.isInCall ? 'in-call' : 'waiting'}`}
                    >
                      <div className="queue-card-header">
                        <div className="job-seeker-info">
                          <div className="avatar">
                            {queueEntry.jobSeeker?.avatarUrl ? (
                              <img src={queueEntry.jobSeeker.avatarUrl} alt="Profile" />
                            ) : (
                              <div className="avatar-placeholder">
                                {queueEntry.jobSeeker?.name?.charAt(0) || '?'}
                              </div>
                            )}
                          </div>
                          <div>
                            <h3>Meeting no. {queueEntry.position}</h3>
                            <p className="job-seeker-name">{queueEntry.jobSeeker?.name || 'Unknown'}</p>
                            <p className="job-seeker-email">{queueEntry.jobSeeker?.email || 'N/A'}</p>
                          </div>
                        </div>

                        <div className="queue-status">
                          {queueEntry.isTest && <TestQueueTag srContext="queue entry" />}
                          {queueEntry.status === 'left_with_message' ? (
                            <span className="status-badge left-message-badge">Left Message</span>
                          ) : queueEntry.status === 'in_meeting' || queueEntry.isInCall ? (
                            <span className="status-badge in-call">In Call</span>
                          ) : (
                            <span className="status-badge waiting">Waiting</span>
                          )}
                        </div>
                      </div>

                      <div className="queue-details">
                        <div className="waiting-row">
                          <div className="waiting-left">
                            <strong>{queueEntry.status === 'in_meeting' || queueEntry.isInCall ? 'In Call:' : 'Waiting:'}</strong>
                            <span className="waiting-timer">{formatDuration(nowTs - new Date(queueEntry.joinedAt).getTime())}</span>
                          </div>
                          <button
                            onClick={async () => {
                              setSelectedJobSeeker(queueEntry);
                              setProfileModalJobSeeker(queueEntry.jobSeeker); // Show queue data immediately
                              setShowDetails(true);
                              // Fetch full job seeker profile to ensure we have linkedInUrl and latest data
                              const jsId = queueEntry.jobSeeker?._id || queueEntry.jobSeeker;
                              if (jsId) {
                                try {
                                  const res = await getUser(typeof jsId === 'string' ? jsId : jsId.toString());
                                  setProfileModalJobSeeker(res?.user || res || queueEntry.jobSeeker);
                                } catch {
                                  // Keep existing profileModalJobSeeker on fetch error
                                }
                              }
                            }}
                            className="btn-details-inline"
                          >
                            View Details
                          </button>
                        </div>
                        {(queueEntry.appliedPosition || queueEntry.appliedLocation || queueEntry.interpreterCategory) && (
                          <dl className="queue-detail-list">
                            {queueEntry.appliedPosition && (
                              <div className="queue-detail-pair">
                                <dt>Position</dt>
                                <dd>{queueEntry.appliedPosition}</dd>
                              </div>
                            )}
                            {queueEntry.appliedLocation && (
                              <div className="queue-detail-pair">
                                <dt>Location</dt>
                                <dd>{queueEntry.appliedLocation}</dd>
                              </div>
                            )}
                            {queueEntry.interpreterCategory && (
                              <div className="queue-detail-pair">
                                <dt>Interpreter</dt>
                                <dd>{queueEntry.interpreterCategory.name}</dd>
                              </div>
                            )}
                          </dl>
                        )}
                        {queueEntry.messageCount > 0 && (
                          <div className="detail-item">
                            <strong>Messages:</strong> {queueEntry.messageCount} unread
                          </div>
                        )}
                      </div>

                      <div className="queue-actions">
                        {queueEntry.status === 'left_with_message' ? (
                          <>
                            <button
                              onClick={() => handleViewMessages(queueEntry)}
                              className="btn-messages"
                              style={{ background: theme.primary, borderColor: theme.primary }}
                            >
                              View Message ({queueEntry.messageCount || 0})
                            </button>
                            <button
                              onClick={() => handleRemoveFromQueue(queueEntry)}
                              className="btn-remove"
                              style={{ background: theme.danger, borderColor: theme.danger }}
                            >
                              Remove
                            </button>
                          </>
                        ) : queueEntry.status === 'invited' || queueEntry.status === 'in_meeting' || queueEntry.isInCall ? (
                          <>
                            <button
                              onClick={() => handleInviteToMeeting(queueEntry)}
                              className="btn-invite"
                              style={{ background: theme.success, borderColor: theme.success }}
                              disabled
                            >
                              Invite
                            </button>

                            <button
                              onClick={() => handleViewMessages(queueEntry)}
                              className="btn-messages"
                              style={{ background: theme.primary, borderColor: theme.primary }}
                              disabled
                            >
                              Messages ({queueEntry.messageCount || 0})
                            </button>

                            <button
                              type="button"
                              onClick={() => handleRemoveFromQueue(queueEntry)}
                              className="btn-remove"
                              style={{ background: theme.danger, borderColor: theme.danger }}
                              title="Remove from queue and end any active video call"
                              aria-label="Remove job seeker from queue and end active call if in progress"
                            >
                              Remove
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleInviteToMeeting(queueEntry)}
                              className="btn-invite"
                              style={inviteLocked ? undefined : { background: theme.success, borderColor: theme.success }}
                              disabled={inviteLocked}
                              title={inviteLocked ? boothClosedNotice : undefined}
                            >
                              Invite
                            </button>

                            <button
                              onClick={() => handleViewMessages(queueEntry)}
                              className="btn-messages"
                              style={{ background: theme.primary, borderColor: theme.primary }}
                            >
                              Messages ({queueEntry.messageCount || 0})
                            </button>

                            <button
                              onClick={() => handleRemoveFromQueue(queueEntry)}
                              className="btn-remove"
                              style={{ background: theme.danger, borderColor: theme.danger }}
                            >
                              Remove
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Chat-style Messages Modal */}
      {showMessagesModal && currentChatEntry && (
        <div className="modal-overlay">
          <div className="modal-content text-messaging-modal">
            <div className="modal-header">
              <h3>Messages - {currentChatEntry.jobSeeker.name}</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowMessagesModal(false);
                  setCurrentChatEntry(null);
                  setMessages([]);
                  setMessageToSend('');
                }}
              >
                ×
              </button>
            </div>

            <div className="messages-container">
              <div className="messages-list">
                {messages.length === 0 ? (
                  <p className="no-messages">No messages yet</p>
                ) : (
                  <>
                    {messages.map((msg, index) => (
                      <div 
                        key={index} 
                        className={`message-bubble ${msg.sender === 'recruiter' ? 'sent' : 'received'}`}
                      >
                        <div className="message-sender">
                          {msg.sender === 'recruiter' ? 'You' : currentChatEntry.jobSeeker.name}
                        </div>
                        {msg.type === 'text' ? (
                          <div className="message-text">{msg.content}</div>
                        ) : msg.type === 'audio' ? (
                          <audio src={msg.content} controls style={{ width: '100%', maxWidth: '250px' }} />
                        ) : (
                          <video
                            src={msg.content}
                            controls
                            style={{ width: '100%', maxWidth: '250px', borderRadius: '8px' }}
                          />
                        )}
                        <div className="message-time">
                          {new Date(msg.createdAt).toLocaleTimeString()}
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              <div className="message-input-area">
                <textarea
                  value={messageToSend}
                  onChange={(e) => setMessageToSend(e.target.value)}
                  placeholder="Type your message..."
                  rows={3}
                  aria-label="Message content"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!messageToSend.trim() || isSendingMessage}
                  className="btn-send-message"
                  type="button"
                >
                  {isSendingMessage ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Broadcast Message Modal */}
      {showBroadcastModal && (
        <div className="modal-overlay">
          <div className="modal-content broadcast-message-modal" role="dialog" aria-modal="true" aria-labelledby="broadcast-message-title">
            <div className="modal-header">
              <h3 id="broadcast-message-title">Broadcast Message</h3>
              <button
                className="modal-close"
                onClick={closeBroadcastModal}
                aria-label="Close broadcast message dialog"
                type="button"
              >
                ×
              </button>
            </div>
            <div className="broadcast-message-body">
              <p className="broadcast-recipient-note">
                This message will be sent to {queue.length} job seeker{queue.length === 1 ? '' : 's'} currently in the queue.
              </p>
              <textarea
                value={broadcastMessage}
                onChange={(e) => setBroadcastMessage(e.target.value)}
                placeholder="Type a message for everyone in the queue..."
                rows={4}
                aria-label="Broadcast message content"
                disabled={isBroadcasting}
              />
              <div className="broadcast-message-actions">
                <button
                  type="button"
                  className="btn-cancel-broadcast"
                  onClick={closeBroadcastModal}
                  disabled={isBroadcasting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-send-broadcast"
                  onClick={handleBroadcastMessage}
                  disabled={!broadcastMessage.trim() || queue.length === 0 || isBroadcasting}
                >
                  {isBroadcasting ? 'Sending...' : 'Send to All'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Job Seeker Profile Modal */}
      {showDetails && selectedJobSeeker && selectedJobSeeker.jobSeeker && (() => {
        const js = profileModalJobSeeker || selectedJobSeeker.jobSeeker;
        return (
        <div className="modal-overlay">
          <div className="modal-content profile-modal">
            <div className="modal-header">
              <h3>Job Seeker Profile</h3>
              <button
                className="modal-close"
                onClick={() => { setShowDetails(false); setProfileModalJobSeeker(null); }}
              >
                ×
              </button>
            </div>

            <div className="profile-body">
              {/* Profile Header */}
              <div className="profile-header">
                <div className="profile-avatar">
                  {js?.avatarUrl ? (
                    <img src={js.avatarUrl} alt="Profile" />
                  ) : (
                    <div className="avatar-placeholder">
                      {js?.name?.charAt(0) || '?'}
                    </div>
                  )}
                  <div className="online-indicator"></div>
                </div>
                <div className="profile-info">
                  <h2 className="profile-name">{js?.name || 'Unknown'}</h2>
                  <p className="profile-email">{js?.email || 'N/A'}</p>
                  {js?.phoneNumber && (
                    <p className="profile-phone">{js.phoneNumber}</p>
                  )}
                  {(js?.city || js?.state) && (
                    <p className="profile-location">
                      {[js?.city, js?.state, js?.country || 'US'].filter(Boolean).join(', ')}
                    </p>
                  )}
                  <div className="profile-actions">
                    {js?.linkedInUrl && (
                      <a
                        href={js.linkedInUrl.startsWith('http') ? js.linkedInUrl : `https://${js.linkedInUrl}`}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="btn-linkedin"
                        aria-label="View LinkedIn profile"
                      >
                        <FaLinkedin size={18} aria-hidden="true" /> LinkedIn
                      </a>
                    )}
                    {js?.resumeUrl ? (
                      <a
                        href={js.resumeUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-resume"
                      >
                        📄 View Complete Resume
                      </a>
                    ) : (
                      <button className="btn-resume" disabled>📄 No Resume Available</button>
                    )}
                  </div>
                </div>
              </div>

              {/* Professional Details */}
              <div className="professional-details">
                <h3>Professional Details</h3>

                <div className="details-grid">
                  {selectedJobSeeker?.appliedPosition && (
                    <div className="detail-section">
                      <h4>Applying For</h4>
                      <div className="detail-content">
                        <div className="detail-item">
                          <span className="detail-label">POSITION</span>
                          <p>{selectedJobSeeker.appliedPosition}</p>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">POSITION LOCATION</span>
                          <p>{selectedJobSeeker.appliedLocation || 'Not specified'}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Professional Summary */}
                  <div className="detail-section">
                    <h4>Professional Summary</h4>
                    <div className="detail-content">
                      <div className="detail-item">
                        <span className="detail-label">PROFESSIONAL HEADLINE</span>
                        <p>{js?.metadata?.profile?.headline || 'Not specified'}</p>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">KEYWORDS & SKILLS</span>
                        <p>{js?.metadata?.profile?.keywords || 'Not specified'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Experience & Employment */}
                  <div className="detail-section">
                    <h4>Experience & Employment</h4>
                    <div className="detail-content">
                      <div className="detail-item">
                        <span className="detail-label">PRIMARY JOB EXPERIENCE</span>
                        {js?.metadata?.profile?.primaryExperience?.length > 0 ? (
                          getLabels(JOB_CATEGORY_LIST, js.metadata.profile.primaryExperience).map((exp, idx) => (
                            <p key={idx}>{exp}</p>
                          ))
                        ) : (
                          <p>Not specified</p>
                        )}
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">EMPLOYMENT TYPES</span>
                        <div className="tags">
                          {js?.metadata?.profile?.employmentTypes?.length > 0 ? (
                            getLabels(JOB_TYPE_LIST, js.metadata.profile.employmentTypes).map((type, idx) => (
                              <span key={idx} className="tag">{type}</span>
                            ))
                          ) : (
                            <span className="tag">Not specified</span>
                          )}
                        </div>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">EXPERIENCE LEVEL</span>
                        <p>{getLabel(EXPERIENCE_LEVEL_LIST, js?.metadata?.profile?.workLevel)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Education & Qualifications */}
                  <div className="detail-section">
                    <h4>Education & Qualifications</h4>
                    <div className="detail-content">
                      <div className="detail-item">
                        <span className="detail-label">HIGHEST EDUCATION LEVEL</span>
                        <p>{getLabel(EDUCATION_LEVEL_LIST, js?.metadata?.profile?.educationLevel)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div className="detail-section">
                    <h4>Additional Information</h4>
                    <div className="detail-content">
                      <div className="detail-item">
                        <span className="detail-label">LANGUAGES</span>
                        <div className="tags">
                          {js?.metadata?.profile?.languages?.length > 0 ? (
                            getLabels(LANGUAGE_LIST, js.metadata.profile.languages).map((lang, idx) => (
                              <span key={idx} className="tag">{lang}</span>
                            ))
                          ) : (
                            <span className="tag">Not specified</span>
                          )}
                        </div>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">VETERAN/MILITARY STATUS</span>
                        <p>{getLabel(MILITARY_EXPERIENCE_LIST, js?.metadata?.profile?.veteranStatus)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* Video Call Component */}
      {isInCall && activeCall && (
        <VideoCall
          callId={typeof activeCall === 'string' ? activeCall : activeCall.id}
          callData={typeof activeCall === 'object' ? activeCall : null}
          onCallEnd={handleCallEnd}
        />
      )}

      {/* Recruiter device selection before inviting */}
      {showRecruiterInviteModal && (
        <CallInviteModal
          recruiterName={user?.name}
          boothName={booth?.name}
          eventName={event?.name}
          audioInputs={audioInputs}
          videoInputs={videoInputs}
          selectedAudioId={selectedAudioId}
          selectedVideoId={selectedVideoId}
          onChangeAudio={setSelectedAudioId}
          onChangeVideo={setSelectedVideoId}
          onAccept={handleRecruiterAcceptInvite}
          onDecline={handleRecruiterDeclineInvite}
        />
      )}

      {/* Rating Modal */}
      {showRatingModal && ratingModalData && (
        <RatingModal
          isOpen={showRatingModal}
          onClose={handleRatingClose}
          onSubmit={handleRatingSubmit}
          jobSeekerName={ratingModalData.jobSeekerName}
          loading={submittingRating}
        />
      )}

      {/* Device Test Modal */}
      <DeviceTestModal
        isOpen={showDeviceModal}
        onClose={handleDeviceCancel}
        audioInputs={audioInputs}
        videoInputs={videoInputs}
        selectedAudioId={selectedAudioId}
        selectedVideoId={selectedVideoId}
        onChangeAudio={setSelectedAudioId}
        onChangeVideo={setSelectedVideoId}
        onSave={handleDeviceSave}
      />
    </div>
  );
}
